import { useCallback, useEffect } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { useUser } from '@/data/User';
import { useSettings } from '@/data/Settings';
import { useExtraRequestParameters } from '@/data/Content/_ExtraRequestParameters';
import { trackDppEvent } from '@/data/dpp/tracker';
import type { DppCouponSource } from '@/data/dpp/types';
import { cartCalculator } from '@/data/Content/_Cart';
import { useCartSWRKey } from '@/data/Content/Cart';
import { transactionsAssignedCoupon, transactionsCoupon } from 'integration/generated/transactions';
import { CouponCouponItem } from 'integration/generated/transactions/data-contracts';
import { stripHtmlTags } from '@/utils/stripHtmlTags';
import {
	clearCouponPending,
	forgetActivatedCoupon,
	markCouponPending,
	readPendingCoupons,
	rememberActivatedCoupon,
	withActivatedCoupons,
} from '@/data/Content/AccountCouponsStorage';
import { Order } from '@/data/types/Order';

const DATA_KEY_ACCOUNT_COUPONS = 'accountCoupons';
const pendingActivations = new Set<string>();
const PAGE_SIZE = 100;
const PERCENTAGE_PATTERN = /(\d{1,3})\s*%/;
const NON_AVAILABLE_STATUSES = ['EXPIRED', 'REDEEMED', 'USED', 'INACTIVE'];

type AccountCouponStatus = 'AVAILABLE' | 'APPLIED';

export type AccountCoupon = {
	id: string;
	code?: string;
	title: string;
	description: string;
	kind: 'REPURCHASE' | 'GENERIC';
	status: AccountCouponStatus;
	promotionId?: string;
	promotionName?: string;
	percentage?: number;
	expirationDateTime?: string;
};

const collectAppliedCodes = (node: unknown, codes: Set<string>) => {
	if (!node || typeof node !== 'object') {
		return;
	}
	const { couponCode } = node as { couponCode?: unknown };
	if (typeof couponCode === 'string') {
		codes.add(couponCode);
	} else if (Array.isArray(couponCode)) {
		couponCode.forEach((item) =>
			typeof item === 'string' ? codes.add(item) : collectAppliedCodes(item, codes)
		);
	}
};

type AssignedCouponItem = Omit<CouponCouponItem, 'couponDescription'> & {
	code?: string;
	description?: CouponCouponItem['couponDescription'];
	promotionIdentifier?: CouponCouponItem['promotion'];
};

export const readAssignedCoupons = (assigned: unknown): CouponCouponItem[] => {
	const { couponCode } = (assigned ?? {}) as { couponCode?: unknown };
	return Array.isArray(couponCode)
		? couponCode
				.filter((item): item is AssignedCouponItem => !!item && typeof item === 'object')
				.map(({ code, description, promotionIdentifier, ...item }) => ({
					...item,
					couponCode: item.couponCode ?? code,
					couponDescription: description,
					promotion: item.promotion ?? promotionIdentifier,
				}))
		: [];
};

const markApplied = (coupon: AccountCoupon): AccountCoupon => ({ ...coupon, status: 'APPLIED' });

const HTML_ENTITIES: Record<string, string> = {
	nbsp: ' ',
	amp: '&',
	lt: '<',
	gt: '>',
	quot: '"',
	iexcl: '¡',
	iquest: '¿',
	aacute: 'á',
	eacute: 'é',
	iacute: 'í',
	oacute: 'ó',
	uacute: 'ú',
	Aacute: 'Á',
	Eacute: 'É',
	Iacute: 'Í',
	Oacute: 'Ó',
	Uacute: 'Ú',
	ntilde: 'ñ',
	Ntilde: 'Ñ',
	uuml: 'ü',
	Uuml: 'Ü',
	laquo: '«',
	raquo: '»',
	deg: '°',
};

const COUPON_TEXT_PATTERN =
	/cup[oó]n\s*t[ií]tulo\s*:\s*(.*?)\s*(?:cup[oó]n\s*descripci[oó]n\s*:\s*(.*))?$/i;

const decodeHtmlEntities = (value: string) =>
	value.replace(/&(#x[\da-f]+|#\d+|[a-z]+);/gi, (entity, code: string) => {
		if (code.startsWith('#')) {
			const isHex = code[1].toLowerCase() === 'x';
			const point = Number.parseInt(code.slice(isHex ? 2 : 1), isHex ? 16 : 10);
			return Number.isFinite(point) ? String.fromCodePoint(point) : entity;
		}
		return HTML_ENTITIES[code] ?? entity;
	});

const toPlainText = (value?: string | null) =>
	decodeHtmlEntities(stripHtmlTags((value ?? '').replace(/<br\s*\/?>/gi, ' ')))
		.replace(/\s+/g, ' ')
		.trim();

const toCouponTexts = (longText: string, shortText: string) => {
	const match = COUPON_TEXT_PATTERN.exec(longText);
	return match
		? { title: match[1].trim(), description: (match[2] ?? '').trim() }
		: { title: longText || shortText, description: '' };
};

const toAccountCoupon = (
	item: CouponCouponItem,
	appliedCodes: Set<string>
): AccountCoupon | null => {
	const id = item.couponId;
	if (!id || NON_AVAILABLE_STATUSES.includes((item.status ?? '').toUpperCase())) {
		return null;
	}
	const texts = item.couponDescription?.[0];
	const shortText = toPlainText(texts?.shortDescription);
	const longText = toPlainText(texts?.longDescription);
	const { title, description } = toCouponTexts(longText, shortText);
	const percentage =
		Number.parseInt(PERCENTAGE_PATTERN.exec(`${title} ${description}`)?.[1] ?? '', 10) || undefined;
	const promotionId = item.promotion?.uniqueID;
	const promotionName = item.promotion?.externalIdentifier?.name;

	return {
		id,
		code: item.couponCode,
		title,
		description,
		kind: percentage ? 'REPURCHASE' : 'GENERIC',
		status: item.couponCode && appliedCodes.has(item.couponCode) ? 'APPLIED' : 'AVAILABLE',
		...(promotionId ? { promotionId } : {}),
		...(promotionName ? { promotionName } : {}),
		...(percentage ? { percentage } : {}),
		...(item.expirationDateTime ? { expirationDateTime: item.expirationDateTime } : {}),
	};
};

export const useAccountCoupons = () => {
	const { user } = useUser();
	const { settings } = useSettings();
	const params = useExtraRequestParameters();
	const { mutate: mutateGlobal } = useSWRConfig();
	const cartKey = useCartSWRKey();
	const storeId = settings?.storeId;
	const userId = user?.isLoggedIn ? user.userId : undefined;
	const { data: cart } = useSWR<Order | undefined>(cartKey);
	const hasOrderItems = !!cart?.orderItem?.length;

	const { data, mutate } = useSWR(
		userId && storeId ? [userId, storeId, DATA_KEY_ACCOUNT_COUPONS] : null,
		async ([id, store]) => {
			const [wallet, assigned] = await Promise.all([
				transactionsCoupon(true).couponGetCoupon(store, { pageSize: PAGE_SIZE }, params),
				transactionsAssignedCoupon(true)
					.cartSelfAssignedCouponDetail(store, {}, params)
					.catch(() => undefined),
			]);
			const appliedCodes = new Set<string>();
			collectAppliedCodes(assigned, appliedCodes);
			const assignedCoupons = readAssignedCoupons(assigned)
				.map((item) => toAccountCoupon(item, appliedCodes))
				.filter((coupon): coupon is AccountCoupon => Boolean(coupon))
				.map(markApplied);
			const assignedIds = new Set(assignedCoupons.map((coupon) => coupon.id));
			const walletCoupons = (wallet.Coupon ?? [])
				.map((item) => toAccountCoupon(item, appliedCodes))
				.filter((coupon): coupon is AccountCoupon => Boolean(coupon));
			const walletIds = new Set(walletCoupons.map((coupon) => coupon.id));
			return withActivatedCoupons(id, [
				...walletCoupons.filter((coupon) => assignedIds.has(coupon.id)).map(markApplied),
				...assignedCoupons.filter((coupon) => !walletIds.has(coupon.id)),
				...walletCoupons.filter((coupon) => !assignedIds.has(coupon.id)),
			]);
		},
		{ revalidateOnFocus: false, shouldRetryOnError: false }
	);

	const activate = useCallback(
		async (couponId: string, source?: DppCouponSource) => {
			if (!storeId || pendingActivations.has(couponId)) {
				return;
			}
			const coupon = data?.find(({ id }) => id === couponId);
			if (!hasOrderItems) {
				if (userId && coupon) {
					rememberActivatedCoupon(userId, coupon);
					markCouponPending(userId, couponId, source);
					await mutate();
				}
				return;
			}
			pendingActivations.add(couponId);
			try {
				await transactionsAssignedCoupon(true)
					.applyCoupon(storeId, { couponId }, {}, params)
					.catch(async (error) => {
						if (userId) {
							clearCouponPending(userId, couponId);
							forgetActivatedCoupon(userId, couponId);
							await mutate();
						}
						throw error;
					});
				if (userId && coupon) {
					rememberActivatedCoupon(userId, coupon);
				}
				if (userId) {
					clearCouponPending(userId, couponId);
				}
				trackDppEvent({ type: 'coupon_applied', source });
				await cartCalculator(true)({ storeId, query: {}, params });
				await Promise.all([mutate(), cartKey ? mutateGlobal(cartKey) : undefined]);
			} finally {
				pendingActivations.delete(couponId);
			}
		},
		[storeId, params, data, userId, hasOrderItems, mutate, mutateGlobal, cartKey]
	);

	return { userId, coupons: data, hasOrderItems, activate };
};

export const useApplyPendingCoupons = () => {
	const { userId, coupons, hasOrderItems, activate } = useAccountCoupons();

	useEffect(() => {
		if (!userId || !coupons || !hasOrderItems) {
			return;
		}
		const listedIds = new Set(coupons.map(({ id }) => id));
		readPendingCoupons(userId)
			.filter(({ id }) => listedIds.has(id))
			.forEach(({ id, source }) => activate(id, source).catch(() => undefined));
	}, [userId, coupons, hasOrderItems, activate]);
};
